<template>
  <v-btn
    v-if="!chatOpen"
    icon="mdi-robot-outline"
    color="#4CAF50"
    size="large"
    class="chat-fab"
    @click="chatOpen = true"
  />

  <v-expand-transition>
    <v-card v-if="chatOpen" class="chat-window d-flex flex-column" theme="dark">
      <v-toolbar color="#1e222d" density="compact" class="border-b">
        <v-list-item
          prepend-avatar="https://cdn-icons-png.flaticon.com/512/4712/4712035.png"
          title="Agente Smartlink"
          subtitle="En línea"
        ></v-list-item>
        <v-spacer />
        <v-btn icon="mdi-close" variant="text" @click="chatOpen = false" />
      </v-toolbar>


      


      <v-card-text ref="chatContainer" class="flex-grow-1 overflow-y-auto pa-4 bg-chat">


        <div 
          v-for="(msg, i) in messages" 
          :key="i" 
          :class="['d-flex mb-3', msg.rol === 'usuario' ? 'justify-end' : 'justify-start']"
        >          
          <v-sheet
            :color="msg.rol === 'usuario' ? '#056162' : '#262d31'"
            class="pa-3 msg-bubble"
            elevation="1"
          >
            <div class="text-body-2">{{ msg.contenido }}</div>
            <div class="text-right text-caption mt-1 opacity-60">12:45</div>
          </v-sheet>
        </div>
        <v-sheet
        v-if="isLoading"
        color="#262d31"
        class="pa-3 msg-bubble"
        elevation="1">
          <div class="text-body-2">{{ messageLoading }}</div>
        </v-sheet>
      </v-card-text>

      <v-divider />
      <v-sheet color="#1e222d" class="pa-3 d-flex align-center">

        
        <v-menu
        v-model="menuOpen"
        :close-on-content-click="false"
        location="bottom start"
        transition="scale-transition"
        >
          <template v-slot:activator="{ props }">
            <v-btn
              v-bind="props"
              icon="mdi-dump-truck"
              color="green"
              elevation="3"
              size="small"
            >
            </v-btn>
          </template>

          <v-card min-width="380" class="pa-3 shadow-lg" color="bgtopology">
            <v-autocomplete
              v-model="selectedDevice"
              :items="devices"
              :custom-filter="filterByFields"
              item-title="name"
              item-value="ip"
              label="Buscar por Nombre o IP..."
              prepend-inner-icon="mdi-magnify"
              variant="outlined"
              density="comfortable"
              return-object
              auto-select-first
              hide-details
              @update:model-value="menuOpen = false"
            >
              <template v-slot:item="{ props, item }">
                <v-list-item
                  v-bind="props"
                  :title="item.raw.name"
                  :subtitle="item.raw.ip"
                >
                  <template v-slot:prepend>
                    <v-icon icon="mdi-ip-network" color="grey"></v-icon>
                  </template>
                </v-list-item>
              </template>
            </v-autocomplete>
          </v-card>
        </v-menu>
        
        <v-text-field
          v-model="userInput"
          placeholder="Escribe un mensaje"
          variant="solo-filled"
          density="compact"
          hide-details
          rounded="xl"
          bg-color="#2a2f32"
          @keyup.enter="handleSend()"
          :disabled="isLoading"
        />

        <v-btn
          v-if="userInput"
          :icon="'mdi-send'"
          :color="'success'"
          variant="text"
          class="ml-2"
          @click="handleSend()"
          :disabled="isLoading"
        />
        <v-btn
          v-else
          :icon=" isRecording ? 'mdi-stop' : 'mdi-microphone'"
          :color="isRecording ? 'red' : 'primary'"
          @click="isRecording ? stopAll() : startAll()"          
          variant="text"
          class="mx-2"
          :disabled="isLoading"
        />        
      </v-sheet>
    </v-card>
  </v-expand-transition>
</template>

<script setup lang="ts">
import { ref, watch, nextTick, onMounted, computed } from 'vue';


/********************************
 ************ STORE *************
 *******************************/
import { useStore } from 'vuex';
const store = useStore();

/********************************
 ************ I18N **************
 *******************************/
import { useI18n } from 'vue-i18n';
import { getUserLoginToken, useChat } from '@/services/Smartlink/IA';
import { LABELS } from '@/constants/labels';
import { getAllInventoryData } from '@/services/Smartlink/Admin';
const { t } = useI18n();


const devices = ref([
  { name: 'Servidor Producción', ip: '192.168.1.10' },
  { name: 'Router Principal', ip: '192.168.1.1' },
  { name: 'NAS Almacenamiento', ip: '192.168.1.50' },
  { name: 'Workstation Diseño', ip: '10.0.0.15' },
]);

const menuOpen = ref(false);
// Aquí se guardará el objeto { name, ip } gracias a la prop "return-object"
const selectedDevice = ref(null);

const filterByFields = (value:any, query:any, item:any) => {
  // Convertimos todo a minúsculas para una búsqueda insensible a mayúsculas
  const searchText = query.toLowerCase();
  const name = item.raw.name.toLowerCase();
  const ip = item.raw.ip.toLowerCase();

  // Retorna true si el texto buscado coincide con el nombre O con la IP
  return name.includes(searchText) || ip.includes(searchText);
};


interface Message {
  rol: 'usuario' | 'asistente';
  contenido: string;
}

const chatOpen = ref(false);
const userInput = ref('');
const chatContainer = ref<HTMLElement | null>(null);
const messages = ref<Message[]>([
  { rol: 'asistente', contenido: 'Bienvenido a Smartlink V2.6. ¿En qué puedo ayudarte hoy?' }
]);

// --- CONFIGURACIÓN DE SPEECH RECOGNITION (Voz a Texto) ---
const setupSpeechRecognition = () => {
  const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
  
  if (!SpeechRecognition) {
    alert("Tu navegador no soporta el reconocimiento de voz.");
    return;
  }

  recognition = new SpeechRecognition();
  recognition.lang = 'es-ES'; // Idioma
  recognition.continuous = true;
  recognition.interimResults = true;

  recognition.onresult = (event: any) => {
    let interimTranscript = '';
    for (let i = event.resultIndex; i < event.results.length; ++i) {
      if (event.results[i].isFinal) {
        transcription.value += event.results[i][0].transcript + ' ';
      } else {
        interimTranscript += event.results[i][0].transcript;
      }
    }
  };
};



// --- ESTADOS ---
const isRecording = ref(false);
const transcription = ref('');
const audioUrl = ref<string | null>(null);

// --- VARIABLES DE NAVEGADOR ---
let mediaRecorder: MediaRecorder | null = null;
let audioChunks: Blob[] = [];
let recognition: any = null;

// --- LOGICA DE GRABACIÓN ---
const startAll = async () => {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    
    // 1. Iniciar Grabación de Audio
    mediaRecorder = new MediaRecorder(stream);
    audioChunks = [];
    
    mediaRecorder.ondataavailable = (e) => audioChunks.push(e.data);
    mediaRecorder.onstop = () => {
      const audioBlob = new Blob(audioChunks, { type: 'audio/webm' });
      audioUrl.value = URL.createObjectURL(audioBlob);
    };

    mediaRecorder.start();

    // 2. Iniciar Reconocimiento de Voz
    if (!recognition) setupSpeechRecognition();
    recognition.start();

    isRecording.value = true;
  } catch (err) {
    console.error("Error al acceder al micrófono:", err);
  }
};

const stopAll = () => {
  mediaRecorder?.stop();
  recognition?.stop();
  isRecording.value = false;

  if(chatOpen.value) {
    setTimeout(() => {
      userInput.value = transcription.value;
      handleSend();
    }, 500);
  }
};




// LOGICA DE AUTO-SCROLL
// Observamos cambios en el array de mensajes
watch(messages, async () => {
  await nextTick(); // Esperamos a que el DOM se actualice
  if (chatContainer.value) {
    const el = chatContainer.value.$el; // En Vuetify el ref accede al componente, .$el al DOM
    el.scrollTo({
      top: el.scrollHeight,
      behavior: 'smooth'
    });
  }
}, { deep: true });

watch(chatOpen, async (isOpen) => {  
  mediaRecorder = null;
  if (!isOpen) {
    stopAll();
  }
})


const TOKEN = ref('');
const CHAT_ID = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);

async function getUserToken() {
  const allData = await getUserLoginToken()
    
    if( allData.status === 400 ){
        store.commit('showSnackBar', {type: LABELS.error, msg: t('snackbar.errorApis'), valor : true});
    }
    if( allData.status === 200 ){
        TOKEN.value = allData.data.token
    }
}

const isLoading = ref(false);
const messageLoading = ref('')

const { messagesChat, connectAndSendMessage} = useChat();

const computedMessagesIA = computed(() => messagesChat.value)

watch(computedMessagesIA.value, (newVal) => {
  if (newVal.length > 0) {
    const lastMessage = newVal[newVal.length - 1];
    if (lastMessage.estado === 'SUCCESS') {
      isLoading.value = false;
      messageLoading.value = '';
      messages.value.push({ rol: 'asistente', contenido: lastMessage.mensaje });
    }
    if (lastMessage.estado === 'LOADING') {
      messageLoading.value = lastMessage.mensaje;
    }
  }
});

const handleSend = () => {
  if (userInput.value.trim()) {

    isLoading.value = true;
    
    messages.value.push({ rol: 'usuario', contenido: userInput.value });

    connectAndSendMessage(TOKEN.value, CHAT_ID, userInput.value, selectedDevice.value || { name: 'No seleccionado', ip: 'N/A' });

    userInput.value = ''; // Limpiar el input

    transcription.value = ''; //Limpiar el audio
  }
};


async function getAllInventory(){

  const allData = await getAllInventoryData()
    
    if( allData.status === 400 ){
        store.commit('showSnackBar', {type: LABELS.error, msg: t('snackbar.errorApis'), valor : true});
    }
    if( allData.status === 200 ){

      devices.value = allData.data.map((item:any) => ({
        name: item.tag,
        ip: item.ip
      }));
        // TOKEN.value = allData.data.token
    }
  
}


onMounted(async () => {
  await getAllInventory();
  await getUserToken();
  // connect(TOKEN.value, CHAT_ID);
 
});



</script>

<style scoped>
.chat-fab {
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 1000;
}

.chat-window {
  position: fixed;
  bottom: 90px;
  right: 20px;
  width: 400px;
  height: 600px;
  max-height: 80vh;
  z-index: 1000;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 12px 28px rgba(0,0,0,0.4);
}

.bg-chat {
  /* Fondo con patrón sutil tipo WhatsApp */
  background-color: #0b141a;
  background-image: url('https://user-images.githubusercontent.com/15075759/28719144-86dc0f70-73b1-11e7-911d-60d70fcded21.png');
  background-repeat: repeat;
  background-opacity: 0.05;
}

.msg-bubble {
  max-width: 80%;
  border-radius: 12px !important;
}

.opacity-60 {
  opacity: 0.6;
}
</style>